import { useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import type { AudioSegment, Scene, SceneImage } from "../types/story";
import { HighlightedText } from "./HighlightedText";
import { useMediaQuery } from "../lib/useMediaQuery";

/**
 * Presentational scene pieces shared by the single page and the book spread.
 *
 * Graceful degradation: missing `image` -> a calm empty page; missing
 * `audio.segments` -> text with no highlighting (handled by HighlightedText).
 */

/** Narration string for a scene: prefer joined segments, else the full text. */
export function sceneText(scene: Scene): string {
  return scene.audio?.segments && scene.audio.segments.length > 0
    ? scene.audio.segments.map((s) => s.text).join(" ")
    : scene.text;
}

/** Which sentence is being spoken now, or -1 if none/unknown. */
function activeSegment(
  segments: AudioSegment[] | undefined,
  currentMs: number
): number {
  if (!segments || segments.length === 0) return -1;
  for (let i = 0; i < segments.length; i++) {
    if (currentMs < segments[i].endMs) return i;
  }
  return segments.length - 1;
}

/**
 * One illustration as a window into a PLACE: the art sits a hair larger than
 * its frame, drifts a few pixels with pointer/tilt (parallax depth, see App),
 * and leans in almost imperceptibly across the narration (`progress` drives a
 * slow Ken-Burns scale — derived from timing the player already has). A soft
 * warm light blooms across it AT each story beat — the picture answering the
 * words a beat later, only between sentences (never under one), and skipped
 * under reduced motion. Missing image -> a calm empty page.
 */
export function Illustration({
  image,
  assetBase,
  segments,
  currentMs = 0,
  progress = 0,
}: {
  image?: SceneImage | null;
  assetBase: string;
  /** When provided, the art reacts gently at each sentence boundary. */
  segments?: AudioSegment[];
  currentMs?: number;
  /** 0..1 through this scene's narration; the picture slowly leans in. */
  progress?: number;
}) {
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const glowRef = useRef<HTMLSpanElement | null>(null);
  const prevSeg = useRef<number | null>(null);

  const seg = activeSegment(segments, currentMs);
  useEffect(() => {
    const prev = prevSeg.current;
    prevSeg.current = seg;
    // Only on a genuine beat change (not the first paint), and only with art.
    if (reduced || prev === null || prev === seg || seg < 0) return;
    const el = glowRef.current;
    if (!el || typeof el.animate !== "function") return;
    el.animate(
      [{ opacity: 0 }, { opacity: 0.16 }, { opacity: 0 }],
      { duration: 900, easing: "ease-in-out" }
    );
  }, [seg, reduced]);

  if (!image?.src) {
    return (
      <div
        className="scene-illustration scene-illustration--empty"
        aria-hidden="true"
      />
    );
  }

  return (
    <div className="scene-illustration">
      <div
        className="illo-parallax"
        style={{ "--kb": Math.min(1, Math.max(0, progress)) } as CSSProperties}
      >
        <img
          src={resolveAsset(image.src, assetBase)}
          alt={image.alt ?? ""}
          draggable={false}
          decoding="async"
        />
      </div>
      <span ref={glowRef} className="illo-glow" aria-hidden="true" />
      <span className="illo-depth" aria-hidden="true" />
    </div>
  );
}

/** Narration text with sentence-synced highlighting. */
export function NarrationText({
  scene,
  currentMs,
}: {
  scene: Scene;
  currentMs: number;
}) {
  return (
    <HighlightedText
      text={sceneText(scene)}
      segments={scene.audio?.segments}
      currentMs={currentMs}
    />
  );
}

/**
 * One scene as a single stacked page: illustration over narration text. Used on
 * phones / portrait, and as the front face of the single-page turn.
 */
export function ScenePlayer({
  scene,
  currentMs,
  assetBase,
}: {
  scene: Scene;
  currentMs: number;
  /** Prefix for relative asset paths (BASE_URL). */
  assetBase: string;
}) {
  return (
    <div className="scene">
      <Illustration
        image={scene.image}
        assetBase={assetBase}
        segments={scene.audio?.segments}
        currentMs={currentMs}
        progress={sceneProgress(scene, currentMs)}
      />
      {/* Keyed by scene id so a new page's line replays its word-order settle. */}
      <NarrationText key={scene.id} scene={scene} currentMs={currentMs} />
    </div>
  );
}

/** How far through this scene's narration we are (0..1), for the lean-in. */
export function sceneProgress(scene: Scene, currentMs: number): number {
  const dur = scene.audio?.durationMs;
  if (!dur || dur <= 0) return 0;
  return Math.min(1, Math.max(0, currentMs / dur));
}

/**
 * Asset `src` in the story JSON are relative; prefix with `assetBase` — the
 * site root in local dev, or the versioned CDN directory in production.
 */
export function resolveAsset(src: string, assetBase: string): string {
  if (/^https?:\/\//.test(src) || src.startsWith("/")) return src;
  return `${assetBase}${src}`;
}
